import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import SideNav from './SideNav';
import TopBar from './TopBar';
import AssistantWidget from '../../features/assistant/AssistantWidget';
import { useAuth } from '../../features/auth/useAuth';
import styles from './AppShell.module.css';

/** Same role gate as SideNav's "Help assistant" nav item. */
const ASSISTANT_WIDGET_ROLES = ['Admin', 'Staff'] as const;

/**
 * AppShell — sidenav | (topbar / main) layout.
 * On screens ≤768px the sidenav becomes an off-canvas drawer.
 */
export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const userRoles = useAuth().user?.roles ?? [];
  const canUseAssistantWidget = ASSISTANT_WIDGET_ROLES.some((r) => userRoles.includes(r));

  // Close sidenav on route change (mobile navigation)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidenav on Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
        hamburgerRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [sidebarOpen]);

  const handleMenuToggle = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.shell}>
      {sidebarOpen && (
        <div className={styles.backdrop} aria-hidden="true" onClick={() => setSidebarOpen(false)} />
      )}

      <div
        id="app-sidebar"
        className={[styles.sidebarWrapper, sidebarOpen ? styles.sidebarOpen : ''].filter(Boolean).join(' ')}
      >
        <SideNav />
      </div>

      <div className={styles.contentColumn}>
        <TopBar onMenuToggle={handleMenuToggle} sidebarOpen={sidebarOpen} menuButtonRef={hamburgerRef} />

        {/* D92/D84 (2026-09-15) — the `key={location.pathname}` remount hack
            that used to live here is GONE. It forced a fresh <main> node per
            route so the browser wouldn't restore a stale scroll offset onto a
            reused node — but that was only necessary because `.main` was its
            own `overflow-y:auto` scroll container (see AppShell.module.css).
            Now that the WINDOW is the scroll container (there is no scrollable
            offset on this node at all — the document has it, and
            `history.scrollRestoration='manual'` in ScrollToTop.tsx governs
            that), remounting <main> on every navigation is unnecessary and
            was already known to have a downside: it discards component state
            (e.g. in-flight transitions) on every route change. Confirmed
            nothing else keys off this node's identity — see ScrollToTop.test.tsx. */}
        <main id="main-content" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      {canUseAssistantWidget && <AssistantWidget />}
    </div>
  );
}
